import { CustomerModel, PaymentMethodModel } from '../../customer/customer.model';

/**
 * Column mapping configuration for flexible CSV parsing
 */
export interface ColumnMapping {
  accountNumberColumn: number;
  amountColumn: number;
  invoiceNumberColumn?: number;
  hasHeaders: boolean;
}

/**
 * A single mapped column stored with both its index and header text.
 * Header text is used to re-match by name when applying a saved mapping
 * to a CSV that may have columns in a different order.
 */
export interface SavedColumnMappingField {
  index: number;
  header: string | null;
}

/**
 * Persisted column mapping for the bulk payment wizard.
 * Stored per distributor profile via the backend API.
 */
export interface SavedColumnMapping {
  hasHeaders: boolean;
  accountNumberColumn: SavedColumnMappingField;
  amountColumn: SavedColumnMappingField;
  invoiceNumberColumn?: SavedColumnMappingField;
}

/**
 * One version entry returned by GET /csv-mappings/:type/:name/versions.
 */
export interface MappingVersion {
  version: number;
  mapping: SavedColumnMapping;
  createdAt: string;
}

/**
 * JSON envelope written to disk on export and expected on import.
 */
export interface MappingExportEnvelope {
  mappingType: string;
  mappingName: string;
  version: number;
  exportedAt: string;
  mapping: SavedColumnMapping;
}

/**
 * Type guard: returns true when obj has the correct SavedColumnMapping shape.
 */
export function isValidSavedColumnMapping(obj: any): obj is SavedColumnMapping {
  if (!obj || typeof obj !== 'object') return false;
  if (typeof obj.hasHeaders !== 'boolean') return false;
  const isField = (f: any) =>
    f && typeof f === 'object' && typeof f.index === 'number' && (f.header === null || typeof f.header === 'string');
  if (!isField(obj.accountNumberColumn)) return false;
  if (!isField(obj.amountColumn)) return false;
  if (obj.invoiceNumberColumn !== undefined && !isField(obj.invoiceNumberColumn)) return false;
  return true;
}

/**
 * Raw CSV row data (string array)
 */
export interface RawCsvRow {
  values: string[];
  rowIndex: number;
}

/**
 * Represents a single row from the uploaded CSV
 */
export interface BulkPaymentRow {
  accountNumber: string;
  amount: number;
  rawAmount: string; // original CSV text, used for the inline edit seed
  isAmountValid: boolean; // false when amount is unparsable, not a clean number, or <= 0
  invoiceNumber?: string;
  rawInvoiceNumber: string; // original CSV text, used for the inline edit seed
  isInvoiceValid: boolean; // false when invoice is blank/missing
  costOfCapitalAmount?: number;
  rowIndex: number; // for tracking original position in CSV
}

/**
 * Represents the matching result for a CSV row
 */
export interface BulkPaymentMatch {
  row: BulkPaymentRow;
  customer?: CustomerModel;
  paymentMethod?: PaymentMethodModel;
  status: 'matched' | 'no-customer' | 'no-payment-method';
  error?: string;
}

/**
 * Represents the result of processing a single payment
 */
export interface BulkPaymentResult {
  row: BulkPaymentRow;
  customer?: CustomerModel;
  status: 'success' | 'failed' | 'pending';
  transactionId?: string;
  error?: string;
  responseMessage?: string;
}

/**
 * Raw CSV data structure
 */
export interface BulkPaymentCsvData {
  'Account Number': string;
  Amount: string;
  'Invoice Number'?: string;
}

/**
 * Summary statistics for bulk payment processing
 */
export interface BulkPaymentSummary {
  total: number;
  matched: number;
  unmatched: number;
  success: number;
  failed: number;
  pending: number;
}
